import type {
  AssignedFilter,
  CallLog,
  Insights,
  Lead,
  LeadEvent,
  LeadStatus,
  IvrConfig,
  IvrOption,
  Me,
  MetaPage,
  Telecaller,
  WaTemplate,
  Followup,
} from './types';

import { API_ENDPOINTS } from './config';
import { apiFetch, API_URL } from './utils/api';
const TOKEN_KEY = 'token';
const EMAIL_KEY = 'email';

// Token and email management (for compatibility)
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (value: string) => localStorage.setItem(TOKEN_KEY, value);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const getEmail = () => localStorage.getItem(EMAIL_KEY) ?? '';
export const setEmail = (value: string) => localStorage.setItem(EMAIL_KEY, value);

// Telecaller phone management (kept for frontend compatibility)
const PHONE_KEY = 'telecaller_phone';
export const getTelecallerPhone = () => localStorage.getItem(PHONE_KEY) ?? '';
export const setTelecallerPhone = (v: string) => localStorage.setItem(PHONE_KEY, v);

export function clearIdentity() {
  clearToken();
  localStorage.removeItem(EMAIL_KEY);
}

export class AuthError extends Error {}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = path.startsWith('http') ? path : API_URL + path;
  
  const res = await apiFetch(url, init);
  if (res.status === 401) {
    clearIdentity();
    throw new AuthError('Session expired — sign in again');
  }
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json() as Promise<T>;
}

export const fetchMe = () => api<{ user: Me }>(`${API_URL}/api/auth/me`);

export const updateMe = (patch: { name?: string; phone?: string }) =>
  api<{ user: Me }>(`${API_URL}/api/auth/me`, { method: 'POST', body: JSON.stringify(patch) });

export const fetchTelecallers = () => api<{ telecallers: Telecaller[] }>(API_ENDPOINTS.TELECALLERS);

export const fetchLeadSection = (
  view: 'queue' | 'unassigned',
  params: { limit?: number; offset?: number; source?: string[] } = {}
) => {
  const qs = new URLSearchParams({ view });
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.source?.length) qs.set('source', params.source.join(','));
  return api<{ leads: Lead[] }>(`${API_ENDPOINTS.LEADS.LIST}?${qs.toString()}`);
};

export const fetchQueue = (assigned: AssignedFilter = '') => {
  if (assigned === 'unassigned') {
    return fetchLeadSection('unassigned');
  }
  return fetchLeadSection('queue');
};

export const fetchAllLeads = (params: {
  status?: string;
  tier?: string;
  source?: string;
  campaigns?: string[];
  leadStatuses?: string[];
  q?: string;
  name?: string;
  phone?: string;
  email?: string;
  leadId?: string;
  assigned?: string;
  clinic?: string;
  tags?: string;
  createdFrom?: string;
  createdTo?: string;
  assignmentFrom?: string;
  assignmentTo?: string;
  dispositionFrom?: string;
  dispositionTo?: string;
  followup?: string;
  followupFrom?: string;
  followupTo?: string;
  includeClosed?: boolean;
  limit?: number;
  offset?: number;
}) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      if (Array.isArray(v)) {
        if (v.length) qs.set(k, v.join(','));
      } else if (typeof v === 'boolean') {
        qs.set(k, v ? 'true' : 'false');
      } else {
        qs.set(k, String(v));
      }
    }
  }
  return api<{ leads: Lead[]; has_more?: boolean }>(API_ENDPOINTS.LEADS.ALL + '?' + qs);
};

export const claimLead = (id: string) =>
  api<{ ok: true; assigned_to: string }>(`${API_URL}/api/leads/${id}/claim`, {
    method: 'POST',
  });

export const fetchLead = (id: string) =>
  api<{ lead: Lead; events: LeadEvent[]; calls: CallLog[] }>(API_ENDPOINTS.LEADS.DETAIL(id));

export const createManualLead = (data: { name: string; phone: string; source?: string; notes?: string }) =>
  api<{ ok: true; lead_id: string }>(`${API_URL}/api/leads/create`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateStatus = (
  id: string,
  status: LeadStatus,
  opts: { note?: string; reason_code?: string; reason_text?: string } = {}
) =>
  api<{ ok: true; from: string; to: string; reason_code: string | null; reason_text: string | null }>(
    API_ENDPOINTS.LEADS.UPDATE_STATUS(id),
    {
      method: 'POST',
      body: JSON.stringify({ status, ...opts }),
    }
  );

export const updateTier = (id: string, tier: 1 | 2 | 3) =>
  api<{ ok: true; from: number; to: number }>(`${API_URL}/api/leads/${id}/tier`, {
    method: 'POST',
    body: JSON.stringify({ tier }),
  });

export const updateLeadName = (id: string, name: string) =>
  api<{ ok: true; name: string }>(`${API_URL}/api/leads/${id}/name`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const assignLead = (id: string, assigned_to: string | null) =>
  api<{ ok: true; assigned_to: string | null }>(API_ENDPOINTS.LEADS.ASSIGN(id), {
    method: 'POST',
    body: JSON.stringify({ assigned_to }),
  });

export const addLeadNote = (id: string, note: string) =>
  api<{ ok: true }>(`${API_URL}/api/leads/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });

export const initCall = (lead_id: string, telecaller_phone: string) =>
  api<{ ok: true }>(API_ENDPOINTS.ACTIONS.CALL, {
    method: 'POST',
    body: JSON.stringify({ lead_id, telecaller_phone }),
  });

export const sendWhatsapp = (
  lead_id: string,
  payload: {
    text?: string;
    template_name?: string;
    params?: string[];
    media?: { type: 'image' | 'video' | 'document'; url: string };
  }
) =>
  api<{ ok: true; provider?: string; raw?: string }>(API_ENDPOINTS.ACTIONS.WHATSAPP, {
    method: 'POST',
    body: JSON.stringify({ lead_id, ...payload }),
  });

export const fetchInsights = (scope: 'team' | 'me' = 'team') =>
  api<Insights>(API_ENDPOINTS.INSIGHTS(scope));

export const fetchWaTemplates = () =>
  api<{ templates: WaTemplate[] }>(API_ENDPOINTS.WA_TEMPLATES);

// ── Admin: Meta Pages ──
export const fetchMetaPages = () =>
  api<{ pages: MetaPage[] }>(API_ENDPOINTS.ADMIN.META_PAGES);

export const upsertMetaPage = (page_id: string, access_token: string, name?: string) =>
  api<{ ok: true; page_id: string; name: string | null }>(API_ENDPOINTS.ADMIN.META_PAGES, {
    method: 'POST',
    body: JSON.stringify({ page_id, access_token, name }),
  });

export const toggleMetaPage = (page_id: string, active: boolean) =>
  api<{ ok: true; active: boolean }>(API_ENDPOINTS.ADMIN.META_PAGES_TOGGLE(page_id), {
    method: 'POST',
    body: JSON.stringify({ active }),
  });

export const deleteMetaPage = (page_id: string) =>
  api<{ ok: true }>(API_ENDPOINTS.ADMIN.META_PAGES_DELETE(page_id), {
    method: 'DELETE',
  });

// ── Admin: IVR configs ──
export const fetchIvrConfigs = () =>
  api<{ configs: IvrConfig[] }>(API_ENDPOINTS.ADMIN.IVR_CONFIGS);

export const upsertIvrConfig = (cfg: {
  campaign: string;
  display_name?: string | null;
  greeting: string;
  greeting_audio_url?: string | null;
  thank_you_audio_url?: string | null;
  options: IvrOption[];
  no_input_tier: number;
  no_input_topic: string;
  no_input_tip?: string | null;
  no_input_opening?: string | null;
  voice: 'woman' | 'man';
  active: boolean;
  business_hours_start?: string;
  business_hours_end?: string;
  business_days?: string;
  timezone?: string;
  after_hours_greeting?: string | null;
  after_hours_greeting_audio_url?: string | null;
  after_hours_options?: IvrOption[];
  support_phone?: string | null;
}) =>
  api<{ ok: true; campaign: string }>(API_ENDPOINTS.ADMIN.IVR_CONFIGS, {
    method: 'POST',
    body: JSON.stringify(cfg),
  });

export const deleteIvrConfig = (campaign: string) =>
  api<{ ok: true }>(API_ENDPOINTS.ADMIN.IVR_CONFIGS_DELETE(campaign), {
    method: 'DELETE',
  });

// ── Follow-ups ──
export const fetchLeadFollowups = (leadId: string) =>
  api<{ followups: Followup[] }>(`${API_URL}/api/leads/${leadId}/followups`);

export const fetchPendingFollowups = () =>
  api<{ followups: Followup[] }>(`${API_URL}/api/followups/pending`);

export const createFollowup = (leadId: string, data: {
  followup_date: string;
  followup_time?: string;
  notes?: string;
}) =>
  api<{ ok: true; id: string }>(`${API_URL}/api/leads/${leadId}/followups`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateFollowupStatus = (followupId: string, status: 'completed' | 'cancelled') =>
  api<{ ok: true; status: string }>(`${API_URL}/api/followups/${followupId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
